import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useStyles } from 'components/Script';
import { ScriptProps } from 'components/Script/type';
import { useScript } from 'hooks';
import { RootState } from 'redux/reducers/type';
import TimeCode from 'commons/utils/TimeCode';
import { API } from 'commons/constants/KeyFlowAPI';
import { assetWithOutToken } from 'commons/utils/commonUtil';
import HostFactory from 'commons/handler/HostFactory';
import DefaultImage from 'asset/images/icon_script_default_player.png';

function Script({
  script,
  isTeam = false,
  id,
  onChangeCurrentTime,
  onChangeInTime,
  onChangeOutTime,
  onChangePartialTitle,
}: ScriptProps) {
  const classes = useStyles();
  const { onSelectScript } = useScript();

  const { selectedScript } = useSelector((state: RootState) => state.script);
  const {
    selectedTeam1,
    selectedTeam2,
    isLoading: isSelectedTeamLoading,
  } = useSelector((state: RootState) => state.asset);
  const { inTime, outTime, fps } = useSelector((state: RootState) => state.video);
  const { isLoadingPut } = useSelector((state: RootState) => state.script);

  const [timecode, setTimecode] = useState<string>(`${script.inTimecode} - ${script.outTimecode}`);
  const [newInTime, setNewInTime] = useState(0);
  const [newOutTime, setNewOutTime] = useState(0);

  const [imgSrc, setImgSrc] = useState('');
  const [isImageLoading, setIsImageLoading] = useState(true);

  const isHomeTeam = script.script.includes(selectedTeam1?.name);
  const isAwayTeam = script.script.includes(selectedTeam2?.name);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  useEffect(() => {
    if (isLoadingPut && selectedScript?.id === script.id) {
      const inTimeCode = new TimeCode(fps);
      inTimeCode.setSeconds(inTime);
      const outTimeCode = new TimeCode(fps);
      outTimeCode.setSeconds(outTime);

      setTimecode(`${inTimeCode.getTimeCode()} - ${outTimeCode.getTimeCode()}`);
      setNewInTime(inTime);
      setNewOutTime(outTime);
    }
  }, [isLoadingPut]);

  const isSelected = useMemo(
    () => (selectedScript ? selectedScript.id === script.id : false),
    [selectedScript, script],
  );

  const onClickScript = useCallback(() => {
    const { inTime, outTime, inPoint, outPoint } = script;
    console.table({
      inTime: newInTime ? newInTime : inTime,
      outTime: newOutTime ? newOutTime : outTime,
      inPoint: inPoint,
      outPoint: outPoint,
    });

    onChangeCurrentTime(newInTime ? newInTime : inTime);
    onChangeInTime(newInTime ? newInTime : inTime);
    onChangeOutTime(newOutTime ? newOutTime : outTime);
    onSelectScript(script);
    onChangePartialTitle(script.script);
  }, [
    script,
    onChangeCurrentTime,
    onChangeInTime,
    onChangeOutTime,
    onSelectScript,
    onChangePartialTitle,
    newInTime,
    newOutTime,
  ]);

  const convertQuarterName = (quarter: string) => {
    if (!quarter) return '';
    const normalized = quarter.trim();

    const match = /^([QX])(\d+)?$/.exec(normalized);
    if (!match) return quarter;

    const type = match[1];
    const num = match[2] ? Number(match[2]) : undefined;

    if (type === 'Q') {
      return num ? `${num}쿼터` : '쿼터';
    } else if (type === 'X') {
      return num ? `${num}연장` : '연장';
    }
  };

  const formattedTitle = useMemo(() => {
    if (!script.script) return '';
    const text = script.script;
    const team1Name = selectedTeam1?.name;
    const team2Name = selectedTeam2?.name;

    // 팀 이름 찾기
    let teamName = '';
    let teamIndex = -1;

    if (team1Name && (teamIndex = text.indexOf(team1Name)) !== -1) {
      teamName = team1Name;
    } else if (team2Name && (teamIndex = text.indexOf(team2Name)) !== -1) {
      teamName = team2Name;
    }
    if (teamIndex === -1) return text;

    // 팀 이름 뒤에 공백이 있으면 그 다음부터 자르기
    const cutIndex = teamIndex + teamName.length;
    let firstLine = text.slice(0, cutIndex).trim();
    let secondLine = text.slice(cutIndex).trim();

    if (secondLine.includes(teamName)) {
      const lastTeamNameIndex = secondLine.lastIndexOf(teamName);
      const newSecondLine =
        secondLine.substring(0, lastTeamNameIndex) +
        '' +
        secondLine.substring(lastTeamNameIndex + teamName.length);
      secondLine = newSecondLine;
    }

    return (
      <>
        {isMobile ? (
          <>
            <span>{firstLine}</span>
            <br />
            <span>{secondLine}</span>
          </>
        ) : (
          <span>
            {firstLine} {secondLine}
          </span>
        )}
      </>
    );
  }, [script.script, selectedTeam1, selectedTeam2, isMobile]);

  useEffect(() => {
    if (!id) {
      setIsImageLoading(false);
      setImgSrc(DefaultImage);
      return;
    }

    setIsImageLoading(true);

    const src = isTeam
      ? assetWithOutToken(`${HostFactory.apiURL}${API.TEAM_LOGO}/${id}`)
      : assetWithOutToken(`${HostFactory.apiURL}${API.PERSON_IMAGE}/${id}`);
    setImgSrc(src);
  }, [id, isTeam]);

  return (
    <div
      className={clsx(
        classes.rowContainer,
        isSelected && classes.selected,
        isSelected && isHomeTeam && classes.selectedHomeTeam,
        isSelected && isAwayTeam && classes.selectedAwayTeam,
        script.type === 'RANGE' && classes.rangeType,
        isSelected && script.type === 'RANGE' && classes.selectedRangeType,
        // 팀 불러오기전에 안보이게 처리
        isSelectedTeamLoading && classes.invisible,
      )}
      onClick={onClickScript}
    >
      {isHomeTeam && (
        <div className={clsx(classes.thumbnailContainer, isTeam && classes.teamThumbnailContainer)}>
          {isImageLoading && <div className={classes.loadingContainer} />}
          <img
            src={imgSrc}
            onLoad={() => setIsImageLoading(false)}
            onError={() => setIsImageLoading(false)}
            className={clsx(
              classes.playerImageContainer,
              isTeam && classes.imageTeamContainer,
              isImageLoading && classes.invisible,
            )}
          />
        </div>
      )}

      <div
        className={clsx(
          classes.contentsContainer,
          isHomeTeam && classes.justifyStart,
          isAwayTeam && classes.justifyEnd,
          !isHomeTeam && !isAwayTeam && classes.justifyCenter,
        )}
      >
        <div
          className={clsx(
            classes.titleContainer,
            isHomeTeam && classes.textAlignLeft,
            isAwayTeam && classes.textAlignRight,
            !isHomeTeam && !isAwayTeam && classes.textAlignCenter,
          )}
        >
          {/*  팀에서 포맷팅할지 숫자에서할지 여쭤보기 */}
          {script.type === 'LOG' ? formattedTitle : script.script}
        </div>
        <div
          className={clsx(
            classes.timeContainer,
            isHomeTeam && classes.justifyStart,
            isAwayTeam && classes.justifyEnd,
            !isHomeTeam && !isAwayTeam && classes.justifyCenter,
          )}
        >
          <p>
            {(isHomeTeam || isAwayTeam) && (
              <>
                {script.quarter && convertQuarterName(script.quarter) + ' | '}
                {script.score && script.score + ' | '}
                {script.gameTime && script.gameTime + ' | '}
              </>
            )}
            {timecode}
          </p>
        </div>
      </div>
      {isAwayTeam && (
        <div className={clsx(classes.thumbnailContainer, isTeam && classes.teamThumbnailContainer)}>
          {isImageLoading && <div className={classes.loadingContainer} />}
          <img
            src={imgSrc}
            onLoad={() => setIsImageLoading(false)}
            onError={() => setIsImageLoading(false)}
            className={clsx(
              classes.playerImageContainer,
              isTeam && classes.imageTeamContainer,
              isImageLoading && classes.invisible,
            )}
          />
        </div>
      )}
    </div>
  );
}

export default Script;
